
import { useState } from 'react'
import { FC, memo, MouseEvent, ReactNode, useCallback } from 'react'
import { CloseSvg, WindowResizeSvg, WindowRightSvg } from '../svgs/Svgs'
import {
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    HeaderTitleIcon,
    Modal,
    ModalBody,
    ModalHeader,
    ModalModeType,
    PositionType,
    WindowSize
} from '../modals/Modal'
import './OrbitalInsights.scss'
import { convertDecimalMinutesToMMSS, mergeClassNames } from '../../../utils/commonFunctions'
import SunExposer from './SunExposer'
import { OrbitalInsightSatelliteType, OrbitStartOptionsType } from './orbital-insights-type'
import PassDetailsPanel from './PassDetailsPanel'
import { useSmartPlacement } from '../modals/hooks/useSmartPlacement'

export type DurationType = 'SEC' | 'MIN'

export type WindowKeys = 'exposure' | 'contacts' | 'features'

interface OrbitalInsightsProps {
    icon: ReactNode
    title: string
    modalId: string
    position: PositionType
    zIndex: number
    className?: string
    targetId: string,
    placement?: 'top' | 'bottom'
    open: boolean
    pinned?: boolean
    windowSize: WindowSize
    data: OrbitalInsightSatelliteType | null;
    onModeClick: (e: MouseEvent, mode: ModalModeType) => void
    orbitStartOption?: OrbitStartOptionsType
}

/**
 * A component that displays orbital insights for a satellite.
 * 
 * @param {CSSProperties} [placement] - Custom placement styles for the modal.
 * @param {string} [className] - Additional class names.
 * @param {boolean} open - Whether the modal is open.
 * @param {boolean} pinned - Whether the modal is pinned.
 * @param {boolean} disabled - Whether the modal is disabled.
 * @param {any} data - The satellite data.
 * @returns {JSX.Element} The orbital insights modal.
 */
const OrbitalInsightDetails: FC<OrbitalInsightsProps> = memo(({
    icon,
    title,
    modalId,
    open,
    data,
    zIndex,
    windowSize,
    position,
    className = '',
    targetId,
    placement = 'top',
    pinned = false,
    onModeClick,
    orbitStartOption = 'Start Time',
}) => {
    const [durationType, setDurationType] = useState<DurationType>('SEC')


    const { top, left, bottom, right } = useSmartPlacement(
        targetId,
        modalId,
        placement,
        open,
        pinned,
        {
            top: position.top,
            right: position.right,
            bottom: position.bottom,
            left: position.left
        }
    );

    const getDuration = useCallback((duration: number) => {
        const minSec = convertDecimalMinutesToMMSS(duration / 60, true)
        return durationType === 'MIN' ? minSec : `${duration}`
    }, [durationType])


    if (!open) return null
    return (
        <Modal id={modalId} open={open} pinned={pinned} className={mergeClassNames(`orbital__insights__modal`, className)}
            style={{
                minWidth: `${position.width}%`,
                height: `${position.height}%`,
                top: top,
                right: right,
                bottom: bottom,
                left: left,
                zIndex: zIndex
            }}
            onClick={(e) => onModeClick(e, 'focus')}
        >
            <ModalHeader className='insights__modal__header'>
                <HeaderTitleContainer>
                    <HeaderTitleIcon className={modalId === 'contacts' ? 'ground__station__icon' : ''}>
                        {icon}
                    </HeaderTitleIcon>
                    <HeaderTitle>
                        {title}
                    </HeaderTitle>
                </HeaderTitleContainer>
                <HeaderActionsContainer>
                    <div className='d-flex align-items-center gap-3'>
                        <div className='d-flex align-items-center gap-2'>
                            <HeaderAction title={pinned ? 'Unpin' : 'Pin to Right'}
                                className={`${pinned && 'active'}`}
                                onClick={(e) => { onModeClick(e, 'pinned') }}
                            >
                                <WindowRightSvg />
                            </HeaderAction>
                            <HeaderAction title='Resize'
                                onClick={(e) => { onModeClick(e, 'resize') }}
                            >
                                <WindowResizeSvg size={windowSize} />
                            </HeaderAction>
                        </div>
                        <div className='d-flex align-items-center gap-2'>
                            <HeaderAction title='Close'
                                onClick={(e) => { onModeClick(e, 'close') }}
                            >
                                <CloseSvg />
                            </HeaderAction>
                        </div>
                    </div>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody className='overflow-hidden'>
                {modalId === 'exposure' && <SunExposer data={data} durationType={durationType} setDurationType={setDurationType} getDuration={getDuration} orbitStartOption={orbitStartOption} />}
                {modalId !== 'exposure' && <PassDetailsPanel data={data} componentType={modalId as 'contacts' | 'features'} durationType={durationType} setDurationType={setDurationType} getDuration={getDuration} orbitStartOption={orbitStartOption} />}
            </ModalBody>
        </Modal >
    )
})

OrbitalInsightDetails.displayName = 'OrbitalInsightDetails'

export default OrbitalInsightDetails